import {
  CheckCircleOutlined,
  DownOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  DatePicker,
  Empty,
  Flex,
  Space,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useAntTheme } from "hooks/useAntTheme";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { AppUser, ChatHistoryResponse } from "types";
import { DateField } from "@refinedev/antd";
import { MarkdownField } from "components/MarkdownField";
import dayjs from "dayjs";
import { t } from "i18n";
import { useChatHistory } from "../hooks/useChatHistory";
import { CHAT_HISTORY_PAGE_SIZE } from "pages/media/constants";
import {
  ArrowRightIcon,
  ChatTeardropIcon,
  DownloadSimpleIcon,
  EnvelopeSimpleIcon,
} from "@phosphor-icons/react";
import { TrackedButton } from "components/TrackedButton";
import { AnalyticsEvents } from "types/analytics";
import { useGetIdentity, useCustomMutation } from "@refinedev/core";
import { CalendarDotsIcon } from "@phosphor-icons/react/dist/ssr";
import { Link } from "react-router-dom";
import { chatHistoryToCsv, downloadCsv } from "./chatHistoryToCsv";

type ChatHistoryParams = {
  page: number;
  size: number;
  from_datetime?: string;
  to_datetime?: string;
};

const STATUS_COLORS: Record<string, string> = {
  Answered: "success",
  NotAnswered: "error",
};

const STATUS_ICON: Record<string, ReactNode> = {
  Answered: <CheckCircleOutlined />,
  NotAnswered: <MinusCircleOutlined />,
};

export const ProjectChatHistory = ({
  projectId,
  enabled = true, // load data on render
  onPeriodChange,
  onHistoryChange,
  onRegisterExportAll,
  startDate,
  endDate,
}: {
  projectId: string;
  enabled: boolean;
  onPeriodChange?: (
    from_datetime: string | undefined,
    to_datetime: string | undefined
  ) => void;
  onHistoryChange?: (history: ChatHistoryResponse[]) => void;
  // Lets the parent fetch every page (not just the ones loaded on screen)
  // before serializing the full history for export.
  onRegisterExportAll?: (
    exportAll: () => Promise<ChatHistoryResponse[]>
  ) => void;
  startDate?: string;
  endDate?: string;
}) => {
  const { theme } = useAntTheme();
  const { RangePicker } = DatePicker;
  const [showAnswer, setShowAnswer] = useState<string | null>();
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [params, setParams] = useState<ChatHistoryParams>({
    page: 1,
    size: CHAT_HISTORY_PAGE_SIZE,
    from_datetime: startDate
      ? dayjs(startDate).format("YYYY-MM-DD")
      : undefined,
    to_datetime: endDate ? dayjs(endDate).format("YYYY-MM-DD") : undefined,
  });
  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } =
    useChatHistory({
      projectId,
      params,
      enabled,
    });

  const updateParams = (params: Partial<ChatHistoryParams>) => {
    setParams((prev) => ({
      ...prev,
      ...params,
    }));
  };

  const applyDateRange = (
    fromDate?: dayjs.Dayjs | null,
    toDate?: dayjs.Dayjs | null
  ) => {
    const formatDate = (date?: dayjs.Dayjs | null) =>
      date ? dayjs(date).format("YYYY-MM-DD") : undefined;

    const from_datetime = formatDate(fromDate);
    const to_datetime = formatDate(toDate);

    updateParams({
      from_datetime,
      to_datetime,
      page: 1,
    });

    if (onPeriodChange) {
      onPeriodChange(from_datetime, to_datetime);
    }
  };

  const chatHistory = useMemo(() => {
    if (!data) return [];
    return data.reduce((acc, page) => {
      return [...acc, ...page.data];
    }, [] as ChatHistoryResponse[]);
  }, [data]);

  // Derive currentSession: use user selection if valid, otherwise default to first session
  const currentSession =
    selectedSession && chatHistory.some((s) => s.session_id === selectedSession)
      ? selectedSession
      : chatHistory[0]?.session_id;

  useEffect(() => {
    onHistoryChange?.(chatHistory);
  }, [chatHistory, onHistoryChange]);

  // The on-screen list is paginated; export needs the complete history, so we
  // walk every remaining page (driving off each fetch result, which is what
  // reflects the freshly-loaded `hasNextPage`) and flatten the accumulated pages.
  const fetchAllPages = useCallback(async (): Promise<
    ChatHistoryResponse[]
  > => {
    let pages = data;
    if (hasNextPage) {
      let result = await fetchNextPage();
      pages = result.data?.pages;
      while (result.hasNextPage) {
        result = await fetchNextPage();
        pages = result.data?.pages;
      }
    }
    return (pages ?? []).reduce<ChatHistoryResponse[]>(
      (acc, page) => [...acc, ...page.data],
      []
    );
  }, [data, hasNextPage, fetchNextPage]);

  useEffect(() => {
    onRegisterExportAll?.(fetchAllPages);
  }, [onRegisterExportAll, fetchAllPages]);

  const items = chatHistory?.map((session) => ({
    label: (
      <Space>
        <DateField
          value={session.history[0]?.timestamp}
          format="L LT"
          style={{
            color:
              currentSession === session.session_id
                ? theme.colorPrimary
                : undefined,
          }}
        />
        <Badge
          count={session.history.length}
          size="small"
          color={
            currentSession === session.session_id
              ? theme.colorPrimary
              : theme.colorTextSecondary
          }
        ></Badge>
      </Space>
    ),
    key: session.session_id,
    children: (
      <Flex
        vertical
        style={{
          padding: 20,
          paddingLeft: 0,
          height: 500,
          overflowY: "scroll",
        }}
        gap={20}
      >
        {session.history.map((chat) => {
          return (
            <Card
              key={chat.timestamp}
              size="small"
              type="inner"
              title={
                <DateField
                  value={chat.timestamp}
                  format="LLL"
                  style={{ fontSize: 12, color: theme.colorTextSecondary }}
                />
              }
              extra={
                <Tag
                  style={{ border: 0, background: "none" }}
                  icon={STATUS_ICON[chat.status]} // Adjust based on session status
                  color={STATUS_COLORS[chat.status]} // Adjust based on session status
                >
                  {t(`projects.components.ProjectChatHistory.${chat.status}`)}
                </Tag>
              }
            >
              <Flex vertical gap={20}>
                <Flex gap={5} vertical>
                  <Flex gap={10}>
                    <Typography.Text strong style={{ width: 70 }}>
                      {t("projects.components.ProjectChatHistory.question")}
                    </Typography.Text>
                    <Typography.Text style={{ width: "80%" }}>
                      {chat.question}
                    </Typography.Text>
                  </Flex>
                  {showAnswer && showAnswer === chat.timestamp && (
                    <Flex gap={10}>
                      <Typography.Text strong style={{ width: 70 }}>
                        {t("projects.components.ProjectChatHistory.answer")}
                      </Typography.Text>
                      <Typography.Text style={{ width: "80%", marginTop: -10 }}>
                        <MarkdownField value={chat.answer} />
                      </Typography.Text>
                    </Flex>
                  )}
                </Flex>
                <Space>
                  <Button
                    size="small"
                    onClick={() =>
                      setShowAnswer(
                        showAnswer === chat.timestamp ? null : chat.timestamp
                      )
                    }
                  >
                    <Typography.Text type="secondary">
                      {showAnswer === chat.timestamp
                        ? t("projects.components.ProjectChatHistory.hideAnswer")
                        : t(
                            "projects.components.ProjectChatHistory.showAnswer"
                          )}
                    </Typography.Text>
                  </Button>
                </Space>
              </Flex>
            </Card>
          );
        })}
      </Flex>
    ),
  }));

  return (
    <Flex vertical style={{ minHeight: 600 }}>
      <Flex
        justify="space-between"
        style={{
          borderBottom: `1px solid ${theme.colorBorderSecondary}`,
          padding: 24,
          paddingTop: 0,
          paddingBottom: 16,
          marginLeft: -24,
          marginRight: -24,
        }}
      >
        <Flex vertical>
          {chatHistory && (
            <Typography.Text>
              {t("projects.components.ProjectChatHistory.chat", {
                count: chatHistory?.length,
              })}
            </Typography.Text>
          )}
        </Flex>
        <RangePicker
          value={[
            params.from_datetime ? dayjs(params.from_datetime) : null,
            params.to_datetime ? dayjs(params.to_datetime) : null,
          ]}
          suffixIcon={<CalendarDotsIcon size={24} color={theme.colorText} />}
          allowEmpty={true}
          size="large"
          onChange={(value) => applyDateRange(value?.[0], value?.[1])}
        />
      </Flex>
      {isLoading && (
        <Flex align="center" justify="center" style={{ flex: 1 }}>
          <Spin />
        </Flex>
      )}
      {chatHistory?.length > 0 && (
        <Tabs
          style={{ marginLeft: -24, marginRight: -24 }}
          tabPosition="left"
          tabBarGutter={0} // more compact list
          size="small"
          items={items}
          onTabClick={(key) => setSelectedSession(key)}
          tabBarExtraContent={
            hasNextPage && (
              <Button
                size="small"
                shape="round"
                onClick={() => fetchNextPage()}
                icon={<DownOutlined />}
                loading={isFetching}
              >
                {t("projects.components.ProjectChatHistory.loadMore")}
              </Button>
            )
          }
        />
      )}
      {chatHistory?.length === 0 && (
        <Flex align="center" justify="center" style={{ flex: 1 }}>
          <Empty
            description={t("projects.components.ProjectChatHistory.noChat")}
          />
        </Flex>
      )}
    </Flex>
  );
};

export const ProjectChatHistoryCard = ({
  projectId,
  projectCreated,
}: {
  projectId: string;
  projectCreated?: string | null;
}) => {
  const { data: userData } = useGetIdentity<AppUser>();
  const { mutateAsync: exportChatHistory } = useCustomMutation();
  const [history, setHistory] = useState<ChatHistoryResponse[]>([]);
  const hasMessagesHistory = history.length > 0;
  const [isExportingCsv, setIsExportingCsv] = useState(false);
  // Set by the child once mounted; pulls every page so the CSV isn't limited to
  // the pages already loaded on screen.
  const exportAllRef = useRef<(() => Promise<ChatHistoryResponse[]>) | null>(
    null
  );

  const handleRegisterExportAll = useCallback(
    (exportAll: () => Promise<ChatHistoryResponse[]>) => {
      exportAllRef.current = exportAll;
    },
    []
  );

  const exportChatMessagesToCsv = async () => {
    setIsExportingCsv(true);
    try {
      const fullHistory = (await exportAllRef.current?.()) ?? history;
      const csv = chatHistoryToCsv(fullHistory, {
        session: t("media.projects.analytics.csv.session"),
        date: t("media.projects.analytics.csv.date"),
        status: t("media.projects.analytics.csv.status"),
        question: t("media.projects.analytics.csv.question"),
        answer: t("media.projects.analytics.csv.answer"),
      });
      downloadCsv(csv, `chat-history-${projectId}.csv`);
    } finally {
      setIsExportingCsv(false);
    }
  };

  const exportChatMessagesToEmail = () => {
    void (async () => {
      const values = {
        project_id: projectId,
      };
      // The export button is only enabled once dates and the user email are
      // present (see canSendEmail), so these fallbacks are never hit at runtime.
      const query = new URLSearchParams({
        from_date: params.from_datetime ?? "",
        to_date: params.to_datetime ?? "",
        recipient_email: userData?.email ?? "",
      }).toString();
      try {
        await exportChatHistory({
          url: `media/projects/${projectId}/chat_history/export?${query}`,
          method: "post",
          values,
          successNotification: {
            message: t(
              `projects.components.ProjectChatHistory.successfulExtraction`
            ),
            type: "success",
          },
        });
      } catch (error) {
        console.error("Exporting chat history failed", error);
      }
    })();
  };
  const [params, setParams] = useState<Partial<ChatHistoryParams>>({
    from_datetime: projectCreated
      ? dayjs(projectCreated).format("YYYY-MM-DD")
      : undefined,
    to_datetime: dayjs().format("YYYY-MM-DD"),
  });

  const handlePeriodChange = (
    from_datetime: string | undefined,
    to_datetime: string | undefined
  ) => {
    setParams((prev) => ({
      ...prev,
      from_datetime,
      to_datetime,
      // page: 1, // reset to first page when changing dates
    }));
  };
  const handleHistoryChange = (history: ChatHistoryResponse[]) => {
    setHistory(history);
  };

  const canSendEmail =
    hasMessagesHistory &&
    params.from_datetime &&
    params.to_datetime &&
    userData?.email;

  return (
    <Card
      type="inner"
      title={
        <Flex align="center" gap={12}>
          <ChatTeardropIcon size={32} weight="duotone" />
          <Typography.Text strong style={{ fontSize: 20 }}>
            {t("media.projects.analytics.chatHistory")}
          </Typography.Text>
        </Flex>
      }
      styles={{
        header: { paddingTop: 16, paddingBottom: 16 },
      }}
      extra={
        <Flex align="center" gap={16}>
          <Link to={`/media/projects/${projectId}/analytics/ai-chatbot`}>
            <Button
              type="link"
              size="large"
              iconPosition="end"
              icon={<ArrowRightIcon size={20} />}
            >
              {t("media.projects.analytics.wantToSeeMore")}
            </Button>
          </Link>
          <Button
            type="default"
            size="large"
            loading={isExportingCsv}
            disabled={!hasMessagesHistory}
            icon={
              <DownloadSimpleIcon
                size={24}
                style={{ verticalAlign: "text-top" }}
              />
            }
            onClick={exportChatMessagesToCsv}
          >
            {t("media.projects.analytics.exportToCsv")}
          </Button>
          <Tooltip
            title={
              canSendEmail
                ? ""
                : t(`projects.components.ProjectChatHistory.selectDates`)
            }
          >
            <TrackedButton
              trackingEventName={AnalyticsEvents.ChatHistoryExport}
              trackingProps={{
                project_id: projectId,
                start_date: params.from_datetime ?? "",
                end_date: params.to_datetime ?? "",
              }}
              type="default"
              size="large"
              disabled={!canSendEmail}
              icon={
                <EnvelopeSimpleIcon
                  size={24}
                  style={{ verticalAlign: "text-top" }}
                />
              }
              onClick={exportChatMessagesToEmail}
            >
              {t("media.projects.analytics.exportToEmail")}
            </TrackedButton>
          </Tooltip>
        </Flex>
      }
    >
      <ProjectChatHistory
        projectId={projectId}
        enabled={true}
        onPeriodChange={handlePeriodChange}
        onHistoryChange={handleHistoryChange}
        onRegisterExportAll={handleRegisterExportAll}
        startDate={params.from_datetime}
        endDate={params.to_datetime}
      />
    </Card>
  );
};
